import { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { ExpressionRow } from './ExpressionRow';
import type { Condition } from '@/types/policy-editor';

interface ConditionBuilderProps {
    conditions: Condition[];
    onChange: (conditions: Condition[]) => void;
    readOnly?: boolean;
    label?: string;
}

export function ConditionBuilder({
    conditions,
    onChange,
    readOnly = false,
    label = 'Conditions',
}: ConditionBuilderProps) {
    const handleAdd = useCallback(() => {
        onChange([
            ...conditions,
            { type: 'comparison', left: '', operator: '==', right: '' },
        ]);
    }, [conditions, onChange]);

    const handleChange = useCallback(
        (index: number, updated: Condition) => {
            const next = [...conditions];
            next[index] = updated;
            onChange(next);
        },
        [conditions, onChange],
    );

    const handleDelete = useCallback(
        (index: number) => {
            onChange(conditions.filter((_, i) => i !== index));
        },
        [conditions, onChange],
    );

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">
                    {label}
                </label>
                {conditions.length > 0 && (
                    <span className="text-[10px] text-slate-400">
                        All conditions must be true (AND)
                    </span>
                )}
            </div>

            {conditions.length === 0 && !readOnly && (
                <p className="text-xs text-slate-400 italic">
                    No conditions — rule always applies. Click below to add one.
                </p>
            )}

            <div className="space-y-2">
                {conditions.map((cond, i) => (
                    <ExpressionRow
                        key={i}
                        condition={cond}
                        onChange={(c) => handleChange(i, c)}
                        onDelete={() => handleDelete(i)}
                        readOnly={readOnly}
                        showAndLabel={i > 0}
                    />
                ))}
            </div>

            {!readOnly && (
                <Button
                    variant="outline"
                    size="sm"
                    className="mt-1 h-7 text-xs"
                    onClick={handleAdd}
                >
                    <Plus size={12} className="mr-1" />
                    Add condition
                </Button>
            )}
        </div>
    );
}
